import React, { useEffect } from 'react';
import { DATE_FORMAT_DISPLAY, DEFAULT_FORMAT, VOUCHER_TYPE_AMOUNT, VOUCHER_TYPE_PERCENT } from '@constants';
import { voucherTypeOptions, voucherUpdateStateOptions } from '@constants/masterData';
import useRestaurant from '@hooks/useRestaurant';
import useTranslate from '@hooks/useTranslate';
import { CheckboxField, DatePickerField, NumericField, SelectField, TextField } from '@itz/react-cms-element';
import { commonMessage } from '@locales/intl';
import { calcCoefficient, convertToUtcEndOfDay, convertToUtcStartOfDay, convertUtcToLocalTime } from '@utils';
import { Col, Form, Row } from 'antd';
import dayjs from 'dayjs';
import customParseFormat from 'dayjs/plugin/customParseFormat';
import utc from 'dayjs/plugin/utc';
dayjs.extend(customParseFormat);
dayjs.extend(utc);

const VoucherForm = ({ detail, form, onSubmit, setIsChangedFormValues, isEditing }) => {
    const translate = useTranslate();
    const typeValue = translate.formatKeys(voucherTypeOptions, ['label']);
    const voucherType = Form.useWatch('type', form);
    const stateValue = translate.formatKeys(voucherUpdateStateOptions, ['label']);
    const startDateValue = Form.useWatch('startDate', form);
    const isRandomCode = Form.useWatch('isRandomCode', form);
    const { currentSettingParse } = useRestaurant();

    useEffect(() => {
        if (detail) {
            form.setFieldsValue({
                name: detail.name,
                code: detail.code,
                type: detail.type,
                value: detail?.type === VOUCHER_TYPE_PERCENT ? detail.value : calcCoefficient(detail.value, 100),
                minOrderValue: calcCoefficient(detail.minOrderValue, 100),
                startDate: detail.startDate
                    ? dayjs(convertUtcToLocalTime(detail.startDate, DEFAULT_FORMAT, DEFAULT_FORMAT), DEFAULT_FORMAT)
                    : null,
                endDate: detail.endDate
                    ? dayjs(convertUtcToLocalTime(detail.endDate, DEFAULT_FORMAT, DEFAULT_FORMAT), DEFAULT_FORMAT)
                    : null,
                state: detail.state,
            });
        } else {
            form.setFieldsValue({
                type: VOUCHER_TYPE_AMOUNT,
            });
        }
    }, [detail]);

    useEffect(() => {
        if (isRandomCode) {
            form.setFieldsValue({ code: null });
        }
    }, [isRandomCode]);

    const handleSubmit = (values) => {
        const preparedData = {
            name: values.name,
            code: values.isRandomCode ? null : values.code,
            type: values.type,
            value: values?.type === VOUCHER_TYPE_PERCENT ? values.value : calcCoefficient(values.value, 0.01),
            minOrderValue: calcCoefficient(values.minOrderValue, 0.01),
            startDate: values.startDate ? convertToUtcStartOfDay(values.startDate) : '',
            endDate: values.endDate ? convertToUtcEndOfDay(values.endDate) : '',
            state: values.state,
        };
        if (detail) preparedData.id = detail.id;
        onSubmit(preparedData);
    };

    return (
        <Form form={form} layout="vertical" onFinish={handleSubmit} onValuesChange={() => setIsChangedFormValues(true)}>
            <Row gutter={24} align="bottom">
                <Col span={12}>
                    <TextField
                        name="name"
                        label={translate.formatMessage(commonMessage.name)}
                        required
                    />
                </Col>
                <Col span={12}>
                    <SelectField
                        name="type"
                        label={translate.formatMessage(commonMessage.voucherType)}
                        required
                        options={typeValue}
                    />
                </Col>
                <Col span={12}>
                    <TextField
                        name="code"
                        label={translate.formatMessage(commonMessage.voucherCode)}
                        disabled={isRandomCode}
                        required={!isRandomCode}
                    />
                </Col>
                <Col span={12}>
                    <CheckboxField
                        name="isRandomCode"
                        optionLabel={translate.formatMessage(commonMessage.randomCode)}
                    />
                </Col>
            </Row>
            <Row gutter={24}>
                <Col span={12}>
                    <NumericField
                        name="value"
                        label={translate.formatMessage(commonMessage.voucherValue)}
                        required
                        min={0}
                        rules={[
                            {
                                validator: (_, value) => {
                                    if (value !== undefined && value !== null && value <= 0) {
                                        return Promise.reject(
                                            new Error(
                                                translate.formatMessage(commonMessage.valueMustBeGreaterThanZero),
                                            ),
                                        );
                                    }

                                    if (
                                        voucherType === VOUCHER_TYPE_PERCENT &&
                                        value !== undefined &&
                                        value !== null &&
                                        value > 100
                                    ) {
                                        return Promise.reject(
                                            new Error(
                                                translate.formatMessage(
                                                    commonMessage.valueMustNotExceed100,
                                                ),
                                            ),
                                        );
                                    }

                                    return Promise.resolve();
                                },
                            },
                        ]}
                        addonAfter={voucherType === VOUCHER_TYPE_PERCENT ? '%' : currentSettingParse.currentcy}
                        formatter={(v) => v}
                        parser={(v) => v}
                    />
                </Col>
                <Col span={12}>
                    <NumericField
                        name="minOrderValue"
                        label={translate.formatMessage(commonMessage.minOrderValue)}
                        required
                        min={0}
                        addonAfter={currentSettingParse.currentcy}
                        formatter={(v) => v}
                        parser={(v) => v}
                        rules={[
                            {
                                validator: (_, value) => {
                                    if (value !== undefined && value !== null && value <= 0) {
                                        return Promise.reject(
                                            new Error(
                                                translate.formatMessage(commonMessage.valueMustBeGreaterThanZero),
                                            ),
                                        );
                                    }
                                    return Promise.resolve();
                                },
                            },
                        ]}
                    />
                </Col>
            </Row>
            <Row gutter={24}>
                <Col span={12}>
                    <DatePickerField
                        name="startDate"
                        label={translate.formatMessage(commonMessage.startDate)}
                        format={DATE_FORMAT_DISPLAY}
                        style={{ width: '100%' }}
                        required
                        disabledDate={(current) => current && current.isBefore(dayjs(), 'day')}
                        rules={[
                            {
                                validator: (_, value) => {
                                    if (value && value.isBefore(dayjs(), 'day')) {
                                        return Promise.reject(
                                            new Error(
                                                translate.formatMessage(commonMessage.startDateMustBeFuture),
                                            ),
                                        );
                                    }
                                    return Promise.resolve();
                                },
                            },
                        ]}
                    />
                </Col>
                <Col span={12}>
                    <DatePickerField
                        name="endDate"
                        label={translate.formatMessage(commonMessage.endDate)}
                        format={DATE_FORMAT_DISPLAY}
                        style={{ width: '100%' }}
                        dependencies={['startDate']}
                        required
                        disabledDate={(current) => {
                            if (current && startDateValue) {
                                return current.isBefore(startDateValue, 'day');
                            }
                            return current && current.isBefore(dayjs(), 'day');
                        }}
                        rules={[
                            {
                                validator: (_, value) => {
                                    if (value && startDateValue && value.isBefore(startDateValue, 'day')) {
                                        return Promise.reject(
                                            new Error(
                                                translate.formatMessage(
                                                    commonMessage.endDateMustBeAfterStart,
                                                ),
                                            ),
                                        );
                                    }
                                    return Promise.resolve();
                                },
                            },
                        ]}
                    />
                </Col>
                {isEditing && (
                    <Col span={12}>
                        <SelectField
                            name="state"
                            label={translate.formatMessage(commonMessage.state)}
                            required
                            options={stateValue}
                        />
                    </Col>
                )}
            </Row>
        </Form>
    );
};

export default VoucherForm;
